"use client";

import { useEffect, useRef, useState } from "react";
import { PrismicRichText, PrismicImage, PrismicLink } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import '@splidejs/react-splide/css/core';
import "./testimonial-slider.css";


/**
 * @typedef {import("@prismicio/client").Content.TestimonialSliderSlice.TestimonialsItem} Testimonial
 * @type {import("react").FC<{testimonial: Testimonial, sliceVariation: string}>}
 */
const TestimonialCard = ({ testimonial, sliceVariation, isCarousel }) => {
  return (
    <div
      className={`bg-beige px-3 py-6 md:p-6 w-full 2xl:max-w-120 h-full rounded-lg ${sliceVariation === "default" ? "text-dark-pink" : "text-dark-green"} flex flex-col gap-5 transition duration-300 ${isCarousel
        ? "group-[.is-visible]:hover:bg-light-orange group-[.is-visible]:hover:scale-105"
        : "group-hover:bg-light-orange group-hover:scale-105"
        }`}
    >
      {isFilled.richText(testimonial.quote) && (
        <PrismicRichText
          field={testimonial.quote}
          components={{
            paragraph: ({ children }) => <p className="p">{children}</p>,
          }}
        />
      )}

      {isFilled.keyText(testimonial.attribution) && (
        <p className="text-attribution">{testimonial.attribution}</p>
      )}
    </div>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.TestimonialSliderSlice} TestimonialSliderSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<TestimonialSliderSlice>} TestimonialSliderProps
 * @type {import("react").FC<TestimonialSliderProps>}
 */
const TestimonialSlider = ({ slice }) => {
  const testimonials = slice.primary.testimonials || [];
  const [deviceType, setDeviceType] = useState("desktop");
  const [isLargeDesktop, setIsLargeDesktop] = useState(false);
  const mobileCarouselRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width < 768) {
        setDeviceType("mobile");
      } else if (width >= 768 && width < 1110) {
        setDeviceType("tablet");
      } else {
        setDeviceType("desktop");
      }

      setIsLargeDesktop(width >= 1200);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const showDesktopSlider = deviceType === "desktop" && (
    (isLargeDesktop && testimonials.length > 4) || 
    (!isLargeDesktop && testimonials.length > 3)
  );
  const showTabletSlider = deviceType === "tablet" && testimonials.length > 2;
  const showDesktopGrid = deviceType === "desktop" && (
    (isLargeDesktop && testimonials.length <= 4) || 
    (!isLargeDesktop && testimonials.length <= 3)
  );
  const showTabletGrid = deviceType === "tablet" && testimonials.length <= 2;
  const showMobileSlider = deviceType === "mobile" && testimonials.length >= 1;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`${slice.variation === "default" ? "bg-moyam-orange" : "bg-dark-green"} py-10 md:py-20 flex flex-col overflow-visible gap-6 md:gap-0`}
    >
      <div className="px-5 md:pl-20 md:pr-60 flex gap-6 text-beige items-center md:items-start">
        {isFilled.image(slice.primary.icon) && (
          <PrismicImage
            field={{
              ...slice.primary.icon,
              alt: slice.primary.icon.alt || "Icône d'illustration"
            }}
            className="h-[30px] md:h-[46.5px] w-auto"
          />
        )}

        {isFilled.richText(slice.primary.title) && (
          <PrismicRichText
            field={slice.primary.title}
            components={{
              heading2: ({ children }) => <h2 className="h2">{children}</h2>,
              heading3: ({ children }) => <h3 className="h2">{children}</h3>,
              heading4: ({ children }) => <h4 className="h2">{children}</h4>,
            }}
          />
        )}
      </div>
      {isFilled.group(slice.primary.testimonials) && (
        <>
          {showMobileSlider && (
            <div className="">
              <div
                className="flex px-5 gap-2 snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] overflow-x-scroll"
              >
                {testimonials.map((testimonial, index) => (
                  <div
                    key={index}
                    className="snap-center snap-always flex-shrink-0 w-[75vw] max-w-[360px]"
                  >
                    <TestimonialCard
                      testimonial={testimonial}
                      sliceVariation={slice.variation}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {showDesktopSlider && (
            <div className="overflow-visible">
              <Splide
                options={{
                  perPage: 4,
                  perMove: 1,
                  gap: "1.5rem",
                  pagination: false,
                  arrows: true,
                  width: "100%",
                  padding: { right: "200px", left: "5rem" },
                  breakpoints: {
                    1199: {
                      perPage: 3,
                    },
                  },
                }}
                aria-label="Témoignages"
              >
                {testimonials.map((testimonial, index) => (
                  <SplideSlide key={index} className="group overflow-visible py-6">
                    <TestimonialCard
                      testimonial={testimonial}
                      sliceVariation={slice.variation}
                      isCarousel={true}
                    />
                  </SplideSlide>
                ))}
              </Splide>
            </div>
          )}

          {showTabletSlider && (
            <div className="overflow-visible">
              <Splide
                options={{
                  perPage: 2,
                  perMove: 1,
                  gap: "1.5rem",
                  pagination: false,
                  arrows: true,
                  width: "100%",
                  padding: { right: "5rem", left: "5rem" },
                }}
                aria-label="Témoignages"
              >
                {testimonials.map((testimonial, index) => (
                  <SplideSlide key={index} className="group overflow-visible py-6">
                    <TestimonialCard
                      testimonial={testimonial}
                      sliceVariation={slice.variation}
                      isCarousel={true}
                    />
                  </SplideSlide>
                ))}
              </Splide>
            </div>
          )}

          {showDesktopGrid && (
            <div className={`px-20 flex flex-row justify-center ${testimonials.length === 4 ? 'gap-3' : 'gap-6'}`}>
              {testimonials.map((testimonial, index) => (
                <div key={index} className={`group py-6 ${testimonials.length === 4 ? 'w-1/4' : 'w-1/3'}`}>
                  <TestimonialCard
                    testimonial={testimonial}
                    sliceVariation={slice.variation}
                    isCarousel={false}
                  />
                </div>
              ))}
            </div>
          )}

          {showTabletGrid && (
            <div className="px-20 flex flex-row gap-6 justify-center">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="group w-1/2 py-6">
                  <TestimonialCard
                    testimonial={testimonial}
                    sliceVariation={slice.variation}
                    isCarousel={false}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {isFilled.link(slice.primary.cta) && (
        <div className="px-20">
          <PrismicLink
            field={slice.primary.cta}
            className={`cta ${slice.variation === "default" ? "cta-beige" : "cta-light-green"} text-center m-auto`}
          >
          </PrismicLink>
        </div>
      )}
    </section>
  );
};

export default TestimonialSlider;
